import { api } from "../../../../api";
import {
  BalanceResponse,
  DdtProps,
  DeliveryCreateDetail,
  DeliveryCreateGeneral,
  DeliveryCreateInfo,
  DeliveryCreateWHSInfo,
  DeliveryDetailItem,
  DeliveryDetails,
  DeliveryDetailsResponse,
  DeliveryGeneral,
  DeliveryReadinessResponse,
  DeliveryResponse,
  DeliveryWHSDetailItem,
  DeliveryWHSDetailsResponse,
  OrderResponse,
  PassivePriceResponse,
} from "./types";

export async function getDeliveryListApi(
  id: number,
  params: { page: number; size: number; order_by?: string },
): Promise<DeliveryResponse> {
  const { data } = await api.get<DeliveryResponse>(
    `/api/shipping-cases/${id}/warehouse-shiftings/without-delivery-whs`,
    { params },
  );
  return data;
}

export async function getWhsListApi(
  id: number,
  params: { page: number; size: number; order_by?: string },
): Promise<DeliveryWHSDetailsResponse> {
  const { data } = await api.get<DeliveryWHSDetailsResponse>(
    `/api/delivery-whs?shipping_case_id=${id}`,
    { params },
  );
  return data;
}

export async function getOrderListApi(
  id: number,
  params: { page: number; size: number; order_by?: string },
): Promise<OrderResponse> {
  const { data } = await api.get<OrderResponse>(
    `/api/shipping-cases/${id}/delivery-info`,
    { params },
  );
  return data;
}

export async function getOrderItemByIdApi(
  deliveryInfoId: number,
): Promise<OrderResponse> {
  const params = { id: deliveryInfoId, size: 1, page: 1 };
  const { data } = await api.get<OrderResponse>(`/api/delivery-info`, {
    params,
  });
  return data;
}

export async function getDeliveryOrderApi(
  id: number,
): Promise<DeliveryGeneral> {
  const { data } = await api.get<DeliveryGeneral>(`/api/deliveries/${id}`);
  return data;
}

export async function getDeliveryOrderDetailsApi(
  id: number,
  isWHS = false,
): Promise<DeliveryDetailsResponse> {
  const { data } = await api.get<DeliveryDetailsResponse>(
    isWHS ? `/api/deliveries/${id}/trucks/whs` : `/api/deliveries/${id}/trucks`,
  );
  return data;
}

export async function createDeliveryOrderGeneralApi(
  id: number,
  params: Partial<DeliveryCreateGeneral>,
): Promise<DeliveryGeneral> {
  const { data } = await api.post<DeliveryGeneral>(
    `/api/shipping-cases/${id}/deliveries`,
    {
      ...params,
    },
  );
  return data;
}

export async function createDeliveryOrderDetailsApi(
  id: number,
  params: Partial<DeliveryCreateDetail>,
  isWHS?: boolean,
): Promise<DeliveryDetails> {
  const payload = { ...params };

  if (isWHS) {
    delete payload.partial_delivery;
  }

  const { data } = await api.post<DeliveryDetails>(
    isWHS ? `/api/deliveries/${id}/trucks/whs` : `/api/deliveries/${id}/trucks`,
    payload,
  );
  return data;
}

export async function createDeliveryInfoApi(
  id: number,
  params: Partial<DeliveryCreateInfo>,
): Promise<DeliveryDetailItem> {
  const { data } = await api.post<DeliveryDetailItem>(
    `/api/delivery-truck/${id}/info`,
    {
      ...params,
    },
  );
  return data;
}

export async function createDeliveryWHSInfoApi(
  id: number,
  params: Partial<DeliveryCreateWHSInfo>,
): Promise<DeliveryWHSDetailItem> {
  const { data } = await api.post<DeliveryWHSDetailItem>(
    `/api/delivery-truck/${id}/whs`,
    {
      ...params,
    },
  );
  return data;
}

export async function updateDeliveryOrderDetailsApi(
  id: number,
  params: Partial<DeliveryCreateDetail>,
): Promise<DeliveryDetailItem> {
  const { data } = await api.patch<DeliveryDetailItem>(
    `/api/delivery-truck/${id}`,
    {
      ...params,
    },
  );
  return data;
}

export async function updateDeliveryInfoApi(
  id: number,
  params: Partial<DeliveryCreateInfo>,
): Promise<DeliveryDetailItem> {
  const { data } = await api.patch<DeliveryDetailItem>(
    `/api/delivery-info/${id}`,
    {
      ...params,
    },
  );
  return data;
}

export async function updateDeliveryWHSInfoApi(
  id: number,
  params: Partial<DeliveryCreateWHSInfo>,
): Promise<DeliveryWHSDetailItem> {
  const { data } = await api.patch<DeliveryWHSDetailItem>(
    `/api/delivery-whs/${id}`,
    {
      ...params,
    },
  );
  return data;
}

export async function updateDeliveryOrderGeneralApi(
  id: number,
  params: Partial<DeliveryCreateGeneral>,
): Promise<DeliveryGeneral> {
  const { data } = await api.patch<DeliveryGeneral>(`/api/deliveries/${id}`, {
    ...params,
  });
  return data;
}

export async function removeOrderInfoApi(id: number): Promise<void> {
  await api.delete<void>(`/api/delivery-info/${id}`);
}

export async function exportDeliveryOrderApi(
  id: number,
  isWhs?: boolean,
): Promise<{
  file: Blob;
  filename: string;
}> {
  const response = await api.get(
    isWhs
      ? `/api/delivery-whs/${id}/export/pdf`
      : `/api/delivery-info/${id}/export/pdf`,
    {
      responseType: "blob",
    },
  );
  const disposition = response.headers["content-disposition"];
  let filename = "downloaded_file";
  if (disposition) {
    const match = disposition.match(/filename="?([^"]+)"?/);
    if (match && match[1]) {
      filename = decodeURIComponent(match[1]);
    }
  }

  return { file: response.data, filename };
}

export async function getBalanceListApi(
  id: number,
  params: { page: number; size: number; order_by?: string },
): Promise<BalanceResponse> {
  const { data } = await api.get<BalanceResponse>(
    `/api/shipping-cases/${id}/delivery-balance-volume`,
    { params },
  );
  return data;
}

export async function exportLoadingDdtApi(
  id: number,
  params: DdtProps,
  isWhs?: boolean,
): Promise<{
  file: Blob;
  filename: string;
}> {
  const response = await api.post(
    isWhs
      ? `/api/loadings-whs/${id}/ddt/export/pdf`
      : `/api/loadings/${id}/ddt/export/pdf`,
    params,
    {
      responseType: "blob",
    },
  );
  const disposition = response.headers["content-disposition"];
  let filename = "downloaded_file";
  if (disposition) {
    const match = disposition.match(/filename="?([^"]+)"?/);
    if (match && match[1]) {
      filename = decodeURIComponent(match[1]);
    }
  }

  return { file: response.data, filename };
}

export async function cancelItem(id: number): Promise<DeliveryDetailItem> {
  const { data } = await api.post<DeliveryDetailItem>(
    `/api/loadings/${id}/cancel`,
  );
  return data;
}

export async function cancelItemById(
  id: number,
): Promise<DeliveryDetailItem | DeliveryWHSDetailItem> {
  const { data } = await api.post<DeliveryDetailItem>(
    `/api/delivery-info/${id}/cancel`,
  );
  return data;
}

export async function cancelWHSItemById(
  id: number,
): Promise<DeliveryDetailItem | DeliveryWHSDetailItem> {
  const { data } = await api.post<DeliveryWHSDetailItem>(
    `/api/delivery-whs/${id}/cancel`,
  );
  return data;
}

export async function getDeliveryReadinessApi(
  id: number,
): Promise<DeliveryReadinessResponse> {
  const { data } = await api.get<DeliveryReadinessResponse>(
    `/api/shipping-cases/${id}/packing-list/delivery-readiness`,
    {
      params: {
        delivery_status__in: "IN_STOCK",
      },
    },
  );
  return data;
}

export async function getPassivePriceApi(
  url: string,
): Promise<PassivePriceResponse> {
  const { data } = await api.get<PassivePriceResponse>(
    `/api/prices/passive?${url}`,
  );
  return data;
}
